import { useLayoutEffect, useRef } from "react";

type Props = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
  inputMode?: "numeric" | "decimal";
  disabled?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

function grouped(value: string) {
  const [whole, fraction] = value.split(".");
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (fraction === undefined ? "" : `.${fraction}`);
}

// [posting.validation-and-review](../../../docs/product/marketplace.md#posting-validation-and-review) / [contracts.workspace-navigation](../../../docs/product/contracts.md#contracts-workspace-navigation): posting, milestone and salary inputs group only the display;
// callers receive the ungrouped draft for validation, totals and submission.
// Counting digits/decimal marks keeps mid-number edits beside the same digit
// when formatting inserts commas, without rounding or converting through Number.
export function GroupedAmountInput({ id, value, onChange, placeholder, className = "field", "aria-label": ariaLabel, inputMode = "decimal", disabled = false, "aria-invalid": invalid, "aria-describedby": describedBy }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cursor = useRef<number | null>(null);
  const display = grouped(value);
  useLayoutEffect(() => {
    if (cursor.current === null || !inputRef.current) return;
    let position = 0;
    let count = 0;
    while (position < display.length && count < cursor.current) {
      if (display[position] !== ",") count += 1;
      position += 1;
    }
    inputRef.current.setSelectionRange(position, position);
    cursor.current = null;
  });
  return <input ref={inputRef} id={id} disabled={disabled} aria-label={ariaLabel} aria-invalid={invalid || undefined} aria-describedby={describedBy} className={className} inputMode={inputMode} value={display} placeholder={placeholder} onChange={(event) => {
    const entered = event.target.value;
    const raw = entered.replace(/[^\d.-]/g, "");
    if (!/^\d*(?:\.\d*)?$/.test(raw)) return;
    cursor.current = entered.slice(0, event.target.selectionStart ?? entered.length).replace(/[^\d.]/g, "").length;
    onChange(raw);
  }} />;
}
